import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { EMPTY, throwError } from 'rxjs';
import { AuthenticationService } from './authentication-service';
import { catchError, map } from 'rxjs/operators';
import { Convert, convertTo } from '@rallycommerce/common/utils.js';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '../user/user.service';
import { requiresRegistrationCard } from '../helpers/signup-variant';
import * as Sentry from '@sentry/angular';

@Injectable()
export class HttpInterceptorService implements HttpInterceptor {
  refreshTokenInProgress = false;

  constructor(private auth: AuthenticationService, private router: Router, private route: ActivatedRoute) {
  }


  intercept(request: HttpRequest<any>, next: HttpHandler): any {
    request = this.buildRequest(request);
    return this.buildNext(next, request);
  }

  private buildRequest(request: HttpRequest<any>) {
    const body = request.body instanceof FormData ? request.body : convertTo(request?.body, Convert.Underscore);
    const headers = {};

    if (this.getHeaderAuthenticationDetails(request)) {
      headers['Authorization'] = this.getHeaderAuthenticationDetails(request);
    }
    request = request.clone({
      setHeaders: headers,
      body: body || null
    });
    return request;
  }

  private buildNext(next: HttpHandler, request: HttpRequest<any>) {
    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const parsedResponse = { body: {} };
          const conditionalConvert = request.url.includes('assets') ? event.body : { data: convertTo(event.body) };
          parsedResponse.body = event.body?.data ?
            {
              data: convertTo(event.body?.data || event.body),
              meta: convertTo(event.body.meta || null),
              sanitizationResult: convertTo(event.body.sanitizationResult || event.body.sanitization_result || null)
            } :
            conditionalConvert;
          return event.clone(parsedResponse);
        } else {
          return event;
        }
      }),
      catchError((res: any) => {
        if (res instanceof HttpErrorResponse) {
          return this.handleResponseError(res);
        }
        return throwError(() => res);
      }));
  }

  private getHeaderAuthenticationDetails(request: HttpRequest<any>): string {
    const token = this.auth.getToken() || '';
    return this.auth.getToken() ? `Bearer ${token}` : '';
  }

  private handleResponseError(response: HttpErrorResponse): any {
    if (response.status === 401 && requiresRegistrationCard(response.error?.signup_variant) && !this.router.url.includes('complete-signup')) {
      // Business-less Variant B/D user (card never added): the API only sets signup_variant
      // on the no-business 401, so this state is always card-less. Route to the public card
      // page to finish signup instead of logging them out — keeps the token intact.
      this.router.navigate(['/complete-signup']);
      return EMPTY;
    }

    if (response.status === 401 && response.error?.requires_email_verification && !this.router.url.includes('register')) {
      // Authenticated but unverified and business-less — e.g. a Variant B/D signup
      // downgraded by the API after the card experiment ended, or a Variant C user
      // returning mid-verification. The API has (re)sent the verification email; land
      // them on the verification screen, whose endpoints (verify/status poll, resend)
      // all return 2xx for a business-less user, so nothing there re-triggers a logout.
      this.router.navigate(['/register', { step: 'email' }]);
      return EMPTY;
    }

    if (response.status === 401 || response.status === 423) {
      const isLoginRoute = this.router.url.includes('login');
      const isInvitationsRoute = this.router.url.includes('invitations');
      const isAllowedRoute = isLoginRoute || isInvitationsRoute;
      const redirectTo = response.status === 423 ? 'oops' : 'login';
      if (!isAllowedRoute || response.status === 423) {
        this.auth.clearAuthData(redirectTo);
      }
      return isAllowedRoute ? throwError(() => response?.error?.message || 'Something went wrong, please try again') : EMPTY;
    } else if (response.status === 404 && response.headers.get('Content-Type') !== 'application/json') {
      this.router.navigateByUrl('');
    }
    const backendMessage = response?.error?.message || response?.message;
    // 5xx bodies carry framework text ("Server Error", stack fragments), never
    // copy meant for users — only 4xx validation messages get surfaced.
    const message = (response.status < 500 && backendMessage) || 'Something went wrong, please try again';
    Sentry.setContext('http_error', {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      message: backendMessage || message,
      error: typeof response.error === 'object' ? JSON.stringify(response.error)?.substring(0, 500) : response.error,
    });
    return throwError(() => message);
  }
}
